import type { ILogger } from 'src/framework/logging/log';
import { isHttpError } from 'src/web-api/base-service';

type HttpRetryClient = {
	get<T>(url: string): Promise<{ data: T }>;
};

export interface HttpRetryOptions {
	maxAttempts?: number;
	baseDelay?: number;
	maxJitter?: number;
	log?: ILogger;
}

/**
 * Performs an HTTP GET request with automatic retry logic for transient failures.
 *
 * @param client - The HTTP client instance to use for the request
 * @param url - The URL to fetch
 * @param options - Configuration options for retry behavior
 * @returns Promise resolving to the response data
 * @throws The last error encountered if all retry attempts fail
 */
export async function getWithRetry<T>(
	client: HttpRetryClient,
	url: string,
	options: HttpRetryOptions = {}
): Promise<{ data: T }> {
	const {
		maxAttempts = 3,
		baseDelay = 100,
		maxJitter = 100,
		log
	} = options;

	let lastError: any;
	let attempt = 0;

	while (attempt < maxAttempts) {
		try {
			// If offline, wait for connection before attempting
			if (!window.navigator.onLine) {
				await new Promise(resolve => {
					window.addEventListener('online', () => resolve(undefined), { once: true });
				});
			}

			const response = await client.get<T>(url);
			if (attempt > 0) {
				log?.trace(`HTTP request succeeded after retry (attempt ${attempt}/${maxAttempts})`, null, { url });
			}

			return response;
		}
		catch (err) {
			lastError = err;
			attempt++;

			// Determine if we should retry
			const shouldRetry = attempt < maxAttempts && isRetryableError(err);

			if (shouldRetry) {
				// Exponential backoff with jitter: base delay * 2^attempt + random jitter
				const delay = baseDelay * Math.pow(2, attempt) + Math.random() * maxJitter;

				await new Promise(resolve => setTimeout(resolve, delay));
			}
			else {
				break;
			}
		}
	}

	// All retries exhausted
	log?.error(new Error('HTTP request failed after all retry attempts'), {
		url,
		attempts: attempt,
		error: lastError?.message || String(lastError)
	});
	throw lastError;
}

/**
 * Determines if an HTTP error should trigger a retry attempt.
 *
 * @param err - The error to evaluate
 * @returns true if the error is likely transient and retrying may succeed
 */
function isRetryableError(err: any): boolean {
	if (isHttpError(err)) {
		// Network errors (no connection, DNS failure, etc.)
		if (err.message === 'Network Error' || err.code === 'ERR_NETWORK')
			return true;

		// No response received (timeout, connection refused, etc.)
		if (!err.response?.status)
			return true;

		// HTTP status codes that indicate transient failures
		const status = err.response.status;
		if (status === 408 							// Request Timeout
			|| status === 429 						// Too Many Requests
			|| (status >= 500 && status < 600)) // Server errors
			return true;
	}

	return false;
}
